<template>
    <restaurant-details-section
        class="restaurant-details-section-module"
        :breadcrumbs="attributes.asyncData.breadcrumbs"
        :lat="restaurantData.location_lat"
        :lng="restaurantData.location_lng"
        :address="restaurantData.address"
        :opening-hours-notice="restaurantData.opening_hours_notice"
        :opening-hours="openingHours"
        :telephone-number="restaurantData.telephone_number"
        :deliveroo-url="restaurantData.deliveroo_url"
        :uber-eats-url="restaurantData.uber_eats_url"
        :just-eat-url="restaurantData.just_eat_url"
        :facilities="facilities"
        :title="attributes.restaurantDetails_title"
        :view-menu-details="restaurantData.view_menu_details"
        :sale-text="restaurantData.sale_text"
        :intro-text="attributes.intro_text"
    />
</template>

<script>
    import RestaurantDetailsSection from '../../../organisms/RestaurantDetailsSection/RestaurantDetailsSection';
    import dayjs from 'dayjs';
    import customParseFormat from 'dayjs/plugin/customParseFormat';
    import formatMediaObject from '../../../../utils/formatters/format-media-object';

    dayjs.extend(customParseFormat);

    export default {
        name: 'restaurant-details-section-module',

        components: { RestaurantDetailsSection },

        props: {
            attributes: {
                type: Object,
                default: () => ({}),
            },
        },

        async asyncData(context) {
            const { default: breadcrumbsQuery } = await import('../../../../queries/breadcrumbs.graphql');

            const locale = context.store.state.locales.locales.find(({ code }) => code === context.store.state.locales.locale ?? 'en')?.code;

            let site = context.$config.SITE_OVERRIDE;

            if (!site) {
                site = context.req
                    ? context.req.headers.host
                        .replace(/https?:\/\//, '')
                    : window.location.host;
            }

            const { pages: [page] } = await context.$strapi.query(breadcrumbsQuery, {
                uri: context.route.path,
                locale,
                site,
                status: context.route.query.preview ? 'DRAFT' : 'PUBLISHED',
            });

            const breadcrumbs = [];

            let currentPage = page;

            // Traverse up the chain, collecting URIs
            while (currentPage) {
                breadcrumbs.unshift({
                    text: currentPage.title,
                    to: currentPage.uri,
                });

                currentPage = currentPage.parent;
            }

            breadcrumbs.unshift({
                text: context.$snippet('home', 'home'),
                to: '/',
            });

            return {
                breadcrumbs,
            };
        },

        computed: {
            /**
             * Safely return the restaurant data.
             *
             * @returns {object}
             */
            restaurantData() {
                const { restaurant } = this.attributes;

                return restaurant || {};
            },

            /**
             * Normalise the opening hours data.
             *
             * @returns {object}
             */
            openingHours() {
                const normalisedTimeFormat = 'HH:mm';

                return [
                    'monday',
                    'tuesday',
                    'wednesday',
                    'thursday',
                    'friday',
                    'saturday',
                    'sunday',
                ].reduce((openingHours, day) => {
                    const {
                        [`${ day }_open`]: open,
                        [`${ day }_close`]: close,
                    } = this.restaurantData;

                    return {
                        ...openingHours,
                        [day]: {
                            open: open
                                ? dayjs(open, 'HH:mm')
                                    .format(normalisedTimeFormat)
                                : null,
                            close: close
                                ? dayjs(close, 'HH:mm')
                                    .format(normalisedTimeFormat)
                                : null,
                        },
                    };
                }, {});
            },

            /**
             * Return the facility names.
             *
             * @returns {Array}
             */
            facilities() {
                if (!this.restaurantData.facilities) {
                    return [];
                }

                return this.restaurantData.facilities
                    .map(({ name, icon }) => ({
                        name,
                        icon: formatMediaObject.call(this, icon),
                    }));
            },
        },
    };
</script>
